import { useLayoutEffect, useMemo, useRef } from 'react';
import type { CellClassParams, ColDef } from 'ag-grid-community';

import type { MergeRowDto } from '../api/mergeResolution.types';

type Col = ColDef<MergeRowDto>;

/**
 * A decision rebuilds the caller's `columnDefs`: the same columns, with new
 * `valueGetter` / `cellClassRules` / `tooltipValueGetter` closures over the
 * decisions. Passing AG Grid a new array every time rebuilds every column, and
 * `ConflictGrid`'s forced cell refresh then repeats the work.
 *
 * `useStableColumnDefs` returns one array for as long as the columns
 * themselves are unchanged. Its three behaviours read the caller's latest
 * defs, so the forced refresh still shows the new decisions.
 */

// A value JSON cannot tell apart (a Map, Set, Date, RegExp or class instance
// all serialise to {} or a string) is compared by identity instead, so a change
// to one still reads as a new structure.
const isPlainJson = (value: object): boolean => {
  const proto = Object.getPrototypeOf(value);
  return Array.isArray(value) || proto === Object.prototype || proto === null;
};

const functionIds = new WeakMap<object, number>();
let nextFunctionId = 0;
const idOf = (fn: object): number => {
  let id = functionIds.get(fn);
  if (id === undefined) {
    id = nextFunctionId++;
    functionIds.set(fn, id);
  }
  return id;
};

const ruleEntries = (col: Col) =>
  Object.entries(col.cellClassRules ?? {}).filter(
    ([, rule]) => typeof rule === 'function'
  );

/** A column def minus the three behaviours that are routed to the latest defs. */
const withoutRouted = (col: Col): Col => {
  if (col.colId === undefined) return col;
  const { valueGetter, tooltipValueGetter, cellClassRules, ...rest }: Col = col;
  return {
    ...rest,
    // What was removed is still part of the column's shape: a getter that
    // appears or goes, or a rule that is added, must read as a new structure.
    valueGetter: typeof valueGetter === 'function' ? undefined : valueGetter,
    tooltipValueGetter:
      typeof tooltipValueGetter === 'function' ? undefined : tooltipValueGetter,
    cellClassRules: Object.fromEntries(
      Object.entries(cellClassRules ?? {}).filter(
        ([, rule]) => typeof rule !== 'function'
      )
    ),
  };
};

/**
 * Identifies the columns' shape. Every function that is not routed counts by
 * identity, so a column whose renderer or class function is rebuilt each time
 * reads as new every time, and the caller's defs are used as they are.
 */
const structureKey = (defs: Col[]): string =>
  JSON.stringify(
    defs.map((col) => [
      withoutRouted(col),
      col.colId !== undefined && typeof col.valueGetter === 'function',
      col.colId !== undefined && typeof col.tooltipValueGetter === 'function',
      col.colId !== undefined ? ruleEntries(col).map(([name]) => name) : [],
    ]),
    (_key, value) => {
      if (typeof value === 'function') {
        return `fn#${idOf(value)}`;
      }
      if (typeof value === 'object' && value !== null && !isPlainJson(value)) {
        return `obj#${idOf(value)}`;
      }
      return value;
    }
  );

const routed = (col: Col, latest: { current: Map<string, Col> }): Col => {
  const colId = col.colId;
  if (colId === undefined) return col;
  const current = () => latest.current.get(colId);

  const out: Col = { ...col };
  if (typeof col.valueGetter === 'function') {
    out.valueGetter = (params) => {
      const getter = current()?.valueGetter;
      return typeof getter === 'function' ? getter(params) : undefined;
    };
  }
  if (typeof col.tooltipValueGetter === 'function') {
    out.tooltipValueGetter = (params) => {
      const getter = current()?.tooltipValueGetter;
      return typeof getter === 'function' ? getter(params) : undefined;
    };
  }
  if (col.cellClassRules) {
    out.cellClassRules = Object.fromEntries(
      Object.entries(col.cellClassRules).map(([name, rule]) => [
        name,
        typeof rule === 'function'
          ? (params: CellClassParams<MergeRowDto>) => {
              const latestRule = current()?.cellClassRules?.[name];
              return typeof latestRule === 'function'
                ? latestRule(params)
                : false;
            }
          : rule,
      ])
    );
  }
  return out;
};

export const useStableColumnDefs = (defs: Col[]): Col[] => {
  // Once per `defs`, not per render: a range drag re-renders the grid.
  const { key, byId } = useMemo(
    () => ({
      key: structureKey(defs),
      byId: new Map(
        defs.flatMap((col) =>
          col.colId === undefined ? [] : [[col.colId, col]]
        )
      ) as Map<string, Col>,
    }),
    [defs]
  );
  // Taken at commit, not in render, so a render that never commits cannot
  // leave the grid reading decisions the screen does not show.
  const latest = useRef(byId);
  useLayoutEffect(() => {
    latest.current = byId;
  }, [byId]);
  // eslint-disable-next-line react-hooks/exhaustive-deps -- `key` is the shape of `defs`; the closures are read from `latest`.
  return useMemo(() => defs.map((col) => routed(col, latest)), [key]);
};
